import { APIClient } from './APIClient';
import { API_URL } from '../settings/local_settings';

export class FieldsColumnsSettingsService {
    /**
     * Obtiene todos los sets de columnas disponibles para el usuario actual en Data Explorer
     * El backend filtra automáticamente los sets disponibles en Data Explorer usando el parámetro ?from=data_explorer
     * @returns {Promise} Promise que resuelve con los sets de columnas filtrados por contexto
     */
    static getFieldsSets() {
        return new APIClient(API_URL + 'looker-cc/fields-columns-settings/?from=data_explorer').call('GET')
            .then((response) => {
                if (!response) {
                    return Promise.reject({
                        status: 'ERROR',
                        errors: [{ message: 'Error de conexión al obtener los sets de columnas' }]
                    });
                }
                // El backend puede devolver códigos de error directamente en status
                if (response.status !== 'OK' && response.status !== 'SUCCESS') {
                    const errorCode = response.error_code || response.status;
                    const errorMessage = response.error_message || 'Error al obtener los sets de columnas';
                    
                    let errors = [];
                    if (Array.isArray(response.errors) && response.errors.length > 0) {
                        errors = response.errors;
                    } else {
                        errors = [{ code: errorCode, message: errorMessage }];
                    }
                    
                    return Promise.reject({
                        status: 'ERROR',
                        errors: errors
                    });
                } else if (response.status === 'OK' || response.status === 'SUCCESS') {
                    return response.data?.fields_sets || response.object?.fields_sets || [];
                }
                return [];
            })
            .catch((error) => {
                // Si el catch captura algo, también retornar un error estructurado
                return Promise.reject({
                    status: 'ERROR',
                    errors: [{ message: error.message || 'Error al obtener los sets de columnas' }]
                });
            });
    }

    /**
     * Crea o actualiza un set de columnas
     * Usa el parámetro ?from=data_explorer para que el backend establezca automáticamente only_available_at si no se especifica
     * @param {Object} fieldsSet - Objeto con los datos del set
     * @param {number} [fieldsSet.id] - ID del set a actualizar (opcional, si no se envía se crea uno nuevo)
     * @param {string} fieldsSet.name - Nombre del set
     * @param {Array<string>} fieldsSet.fields - Lista de nombres de fields
     * @param {boolean} [fieldsSet.is_shared=false] - Si es compartido con la agencia
     * @param {string|null} [fieldsSet.only_available_at=null] - Restricción de uso (opcional, se establece automáticamente si se usa ?from)
     * @param {string} fieldsSet.data_source_type - Tipo de fuente de datos: "BUSINESS_UNITS" o "AD_ACCOUNTS"
     * @returns {Promise} Promise que resuelve con el set creado/actualizado
     */
    static saveFieldsSet(fieldsSet) {
        return new APIClient(API_URL + 'looker-cc/fields-columns-settings/?from=data_explorer').call('POST', fieldsSet)
            .then((response) => {
                if (!response) {
                    return Promise.reject({
                        status: 'ERROR',
                        errors: [{ message: 'Error de conexión al guardar el set de columnas' }]
                    });
                }
                // El backend devuelve el código de error directamente en status (ej: 'DUPLICATE_PRIVATE_NAME')
                // y errors puede ser un objeto vacío {} en lugar de un array
                if (response.status !== 'OK' && response.status !== 'SUCCESS') {
                    // Construir el error usando error_code o status, y error_message
                    const errorCode = response.error_code || response.status;
                    const errorMessage = response.error_message || 'Error al guardar el set de columnas';
                    
                    // Si errors es un array con elementos, usarlo; si no, construir uno desde las columnas del response
                    let errors = [];
                    if (Array.isArray(response.errors) && response.errors.length > 0) {
                        errors = response.errors;
                    } else {
                        errors = [{ 
                            code: errorCode,
                            message: errorMessage
                        }];
                    }
                    
                    return Promise.reject({
                        status: 'ERROR',
                        errors: errors
                    });
                } else if (response.status === 'OK' || response.status === 'SUCCESS') {
                    return response.data || response.object;
                }
                return null;
            })
    }

    /**
     * Borra un set de columnas privado
     * @param {number} fieldsSetId - ID del set a borrar
     * @returns {Promise} Promise que resuelve con el resultado de la eliminación
     */
    static deleteFieldsSet(fieldsSetId) {
        return new APIClient(API_URL + `looker-cc/fields-columns-settings/${fieldsSetId}/`).call('DELETE')
            .then((response) => {
                if (!response) {
                    return Promise.reject({
                        status: 'ERROR',
                        errors: [{ message: 'Error de conexión al borrar el set de columnas' }]
                    });
                }
                // El backend puede devolver códigos de error directamente en status
                if (response.status !== 'OK' && response.status !== 'SUCCESS') {
                    const errorCode = response.error_code || response.status;
                    const errorMessage = response.error_message || 'Error al borrar el set de columnas';
                    
                    let errors = [];
                    if (Array.isArray(response.errors) && response.errors.length > 0) {
                        errors = response.errors;
                    } else {
                        errors = [{ code: errorCode, message: errorMessage }];
                    }
                    
                    return Promise.reject({
                        status: 'ERROR',
                        errors: errors
                    });
                } else if (response.status === 'OK' || response.status === 'SUCCESS') {
                    return response.data || response.object;
                }
                return null;
            })
            .catch((error) => {
                return Promise.reject({
                    status: 'ERROR',
                    errors: [{ message: error.message || 'Error al borrar el set de columnas' }]
                });
            });
    }

    /**
     * Filtra los sets por tipo de fuente de datos
     * Nota: El filtro por contexto (only_available_at) ya se aplica automáticamente por el backend con el parámetro ?from=data_explorer
     * @param {Array} fieldsSets - Lista de todos los sets (ya filtrados por contexto por el backend)
     * @param {string} dataSourceType - Tipo de fuente de datos: "BUSINESS_UNITS" o "AD_ACCOUNTS"
     * @returns {Array} Sets filtrados por data_source_type
     */
    static filterSetsForDataExplorer(fieldsSets, dataSourceType) {
        // Solo filtrar por data_source_type, el filtro de contexto ya lo hace el backend
        return fieldsSets.filter(set => set.data_source_type === dataSourceType);
    }
}

